/**
 * Text sanitisation shared by the backend and by the schemas in this package.
 *
 * It lives here because `contracts/` compiles with `rootDir: src` and cannot
 * import from the backend tree, while the design-spec schemas sanitise their
 * cells at ingest (see designSpec.ts). The backend re-exports these from
 * `src/services/firmPlaybooks/sanitize.ts`, which was their previous home.
 */

// The invisible / direction-spoofing character set, built from explicit code
// point ranges so there are NO literal invisibles in source (they corrupt on
// edit). Covers C0/C1 controls, soft hyphen, bidi controls, zero-width
// characters, BOM, interlinear annotation, and Unicode tag characters. We keep
// TAB(0x09), LF(0x0A) and CR(0x0D) — real whitespace handled elsewhere.
const INVISIBLE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x00, 0x08], [0x0b, 0x0c], [0x0e, 0x1f], // C0 controls (minus \t \n \r)
  [0x7f, 0x9f],                             // DEL + C1 controls
  [0x00ad, 0x00ad],                         // soft hyphen
  [0x061c, 0x061c],                         // arabic letter mark
  [0x180e, 0x180e],                         // mongolian vowel separator
  [0x200b, 0x200f],                         // zero-width + LRM/RLM
  [0x202a, 0x202e],                         // bidi embeddings / overrides
  [0x2060, 0x2064],                         // word joiner + invisible operators
  [0x2066, 0x206f],                         // bidi isolates + deprecated format
  [0xfeff, 0xfeff],                         // BOM / zero-width no-break space
  [0xfff9, 0xfffb],                         // interlinear annotation
  [0xe0000, 0xe007f],                       // Unicode tag characters
];

const INVISIBLE_OR_BIDI = new RegExp(
  '[' +
    INVISIBLE_RANGES.map(([lo, hi]) =>
      lo === hi
        ? `\\u{${lo.toString(16)}}`
        : `\\u{${lo.toString(16)}}-\\u{${hi.toString(16)}}`,
    ).join('') +
    ']',
  'gu',
);

/** Strip invisible/bidi characters everywhere (body-safe: preserves \n, \t, \r). */
export function stripInvisible(input: string): string {
  return input.replace(INVISIBLE_OR_BIDI, '');
}
